import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  listChats,
  listMessages,
  markChatRead,
  sendTextMessage,
  getWhatsAppErrorMessage,
  isWhatsAppForbiddenError,
} from "../../services/whatsappApi";
import {
  formatRelativeTime,
  formatWhatsAppMobile,
  formatCompactWhatsAppMobile,
  getChatDisplayName,
  getInitials,
  getMessagePreviewText,
  getMessageMediaUrl,
  getMessageCaption,
  getDocumentFilename,
  getMessageType,
  sortMessagesChronologically,
} from "../../utils/whatsappFormat";
import {
  getIncomingMessageKey,
  isWhatsAppSoundMuted,
  playWhatsAppMessageSound,
  setWhatsAppSoundMuted,
  unlockWhatsAppNotificationSound,
  getOutgoingMessageMetaId,
} from "../../utils/whatsappNotificationSound";
import { useWhatsAppInboxSocket } from "../../hooks/useWhatsAppInboxSocket";
import ChatRightSidebar from "../../components/whatsapp/inbox/ChatRightSidebar";
import SendTemplateModal from "../../components/whatsapp/inbox/SendTemplateModal";
import WhatsAppImagePreviewModal from "../../components/whatsapp/inbox/WhatsAppImagePreviewModal";
import ChatEmojiPicker from "../../components/whatsapp/inbox/ChatEmojiPicker";
import ChatAttachmentMenu from "../../components/whatsapp/inbox/ChatAttachmentMenu";
import SendChatMediaModal from "../../components/whatsapp/inbox/SendChatMediaModal";
import "./whatsapp.css";
import WhatsAppPageShell from "./WhatsAppPageShell";

function resetMainScrollTop() {
  const main = document.querySelector(".app-main-scroll");
  if (main) main.scrollTop = 0;
}

const CHAT_PAGE_SIZE = 20;
const MESSAGE_PAGE_SIZE = 50;
const FALLBACK_POLL_MS = 60000;
const SEARCH_DEBOUNCE_MS = 300;
const SCROLL_NEAR_BOTTOM_PX = 80;
const SCROLL_LOAD_OLDER_PX = 60;
const CHAT_FILTERS = [
  { id: "all", label: "All" },
  {
    id: "read",
    label: "Read",
    icon: "solar:chat-round-check-linear",
  },
  {
    id: "unread",
    label: "Unread",
    icon: "solar:chat-round-unread-linear",
  },
];

function isUnreadChat(chat) {
  return Number(chat?.unreadCount) > 0;
}

function reportWhatsAppError(err, fallback, { silent = false } = {}) {
  const msg = getWhatsAppErrorMessage(err, fallback);
  if (!silent || isWhatsAppForbiddenError(err)) {
    toast.error(msg);
  }
  return msg;
}

function ChatListSkeleton() {
  return (
    <>
      {[1, 2, 3, 4, 5].map((key) => (
        <div key={key} className="whatsapp-skeleton-row">
          <div className="whatsapp-skeleton whatsapp-skeleton-avatar" />
          <div className="whatsapp-skeleton-lines">
            <div
              className="whatsapp-skeleton whatsapp-skeleton-line"
              style={{ width: "55%" }}
            />
            <div
              className="whatsapp-skeleton whatsapp-skeleton-line"
              style={{ width: "80%" }}
            />
          </div>
        </div>
      ))}
    </>
  );
}

function MessagesSkeleton() {
  return (
    <div className="whatsapp-messages">
      <div
        className="whatsapp-skeleton whatsapp-skeleton-bubble"
        style={{ width: "45%" }}
      />
      <div
        className="whatsapp-skeleton whatsapp-skeleton-bubble out"
        style={{ width: "50%" }}
      />
      <div
        className="whatsapp-skeleton whatsapp-skeleton-bubble"
        style={{ width: "40%" }}
      />
      <div
        className="whatsapp-skeleton whatsapp-skeleton-bubble out"
        style={{ width: "35%" }}
      />
    </div>
  );
}

function getDeliveryTickClass(status) {
  const s = String(status || "").toUpperCase();
  if (s === "READ" || s === "SEEN") return "wa-tick--read";
  if (s === "DELIVERED" || s === "DELIVERY") return "wa-tick--delivered";
  if (s === "SENT" || s === "ACCEPTED") return "wa-tick--sent";
  if (s === "FAILED" || s === "ERROR") return "wa-tick--failed";
  return "wa-tick--sent";
}

function MessageBubble({ message, onImageClick }) {
  const isOutgoing = String(message.direction || "").toUpperCase() === "OUTGOING";
  const type = getMessageType(message);
  const text = getMessagePreviewText(message);
  const mediaUrl = getMessageMediaUrl(message);
  const time = formatRelativeTime(message.timestamp);
  const tickClass = getDeliveryTickClass(message.status);
  const caption = getMessageCaption(message);
  const docFilename = getDocumentFilename(message);
  const normalizedType = type === "voice" ? "audio" : type;

  return (
    <div
      className={`whatsapp-bubble-wrap ${isOutgoing ? "outgoing" : "incoming"}`}
    >
      <div className={`whatsapp-bubble ${isOutgoing ? "outgoing" : "incoming"}`}>
        {normalizedType === "image" && mediaUrl ? (
          <div className="wa-bubble-media">
            <button
              type="button"
              className="wa-bubble-image-btn"
              onClick={() =>
                onImageClick?.(mediaUrl, caption || "Image")
              }
              aria-label="Open image preview"
            >
              <img
                src={mediaUrl}
                alt={caption || "Image"}
                className="wa-bubble-image"
                loading="lazy"
              />
            </button>
            {caption ? (
              <div className="whatsapp-bubble-text">{caption}</div>
            ) : null}
          </div>
        ) : normalizedType === "video" && mediaUrl ? (
          <div className="wa-bubble-media">
            <video
              src={mediaUrl}
              controls
              className="wa-bubble-video"
              preload="metadata"
            />
            {caption ? (
              <div className="whatsapp-bubble-text">{caption}</div>
            ) : null}
          </div>
        ) : normalizedType === "audio" && mediaUrl ? (
          <div className="wa-bubble-media">
            <audio
              src={mediaUrl}
              controls
              className="wa-bubble-audio"
              preload="metadata"
            />
          </div>
        ) : normalizedType === "document" && mediaUrl ? (
          <div className="wa-bubble-media">
            <a
              href={mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="wa-bubble-document"
            >
              <Icon icon="solar:document-text-bold" width={22} />
              <span>{docFilename}</span>
            </a>
            {caption ? (
              <div className="whatsapp-bubble-text">{caption}</div>
            ) : null}
          </div>
        ) : (
          <div className="whatsapp-bubble-text">{text}</div>
        )}
        <div className="whatsapp-bubble-meta">
          <span className="whatsapp-bubble-time">{time}</span>
          {isOutgoing ? (
            <span className={`wa-ticks ${tickClass}`} aria-label={message.status || "sent"}>
              {String(message.status || "").toUpperCase() === "FAILED" ? (
                <Icon icon="solar:danger-circle-bold" width={14} />
              ) : (
                <Icon icon="solar:check-read-linear" width={16} />
              )}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function WhatsAppInbox() {
  const { chatId: routeChatId } = useParams();
  const navigate = useNavigate();
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const composerTextareaRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const attachButtonRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const pollTimerRef = useRef(null);
  const shouldStickToBottomRef = useRef(true);
  const messagesPageOffsetRef = useRef(0);
  const chatPollTimerRef = useRef(null);
  const activeChatIdRef = useRef(null);
  const socketConnectedRef = useRef(false);

  const [socketConnected, setSocketConnected] = useState(false);
  const [soundMuted, setSoundMuted] = useState(() => isWhatsAppSoundMuted());

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [chatFilter, setChatFilter] = useState("all");
  const [chats, setChats] = useState([]);
  const [chatsTotal, setChatsTotal] = useState(0);
  const [chatsLoading, setChatsLoading] = useState(true);
  const [chatsLoadingMore, setChatsLoadingMore] = useState(false);
  const [chatsError, setChatsError] = useState("");
  const [messages, setMessages] = useState([]);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [messagesPageOffset, setMessagesPageOffset] = useState(0);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [messagesError, setMessagesError] = useState("");
  const [composerText, setComposerText] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [sidebarRefreshToken, setSidebarRefreshToken] = useState(0);
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: null,
    alt: "Preview",
  });
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [mediaModal, setMediaModal] = useState({ show: false, type: "image" });

  const openImageModal = useCallback((imageUrl, alt = "Preview") => {
    if (!imageUrl) return;
    setImageModal({
      isOpen: true,
      imageUrl,
      alt,
    });
  }, []);

  const closeImageModal = useCallback(() => {
    setImageModal({
      isOpen: false,
      imageUrl: null,
      alt: "Preview",
    });
  }, []);

  const selectedChatId = routeChatId || null;
  activeChatIdRef.current = selectedChatId;
  socketConnectedRef.current = socketConnected;
  const selectedChat = useMemo(
    () => chats.find((c) => c.chatId === selectedChatId) || null,
    [chats, selectedChatId],
  );

  const scrollMessagesToBottom = useCallback((behavior = "auto") => {
    const el = messagesContainerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (behavior === "smooth") {
          el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        } else {
          el.scrollTop = el.scrollHeight;
        }
      });
    });
  }, []);

  const isNearBottom = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return true;
    return (
      el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_NEAR_BOTTOM_PX
    );
  }, []);

  const fetchChats = useCallback(
    async ({ offset = 0, append = false, silent = false } = {}) => {
      if (append) {
        setChatsLoadingMore(true);
      } else if (!silent) {
        setChatsLoading(true);
      }
      if (!silent) {
        setChatsError("");
      }

      try {
        const res = await listChats({
          search: searchQuery,
          limit: CHAT_PAGE_SIZE,
          offset,
        });
        const rows = Array.isArray(res?.data) ? res.data : [];
        const total = Number(res?.total) || rows.length;
        setChats((prev) => (append ? [...prev, ...rows] : rows));
        setChatsTotal(total);
      } catch (err) {
        const msg = getWhatsAppErrorMessage(err, "Failed to load chats");
        if (!silent) {
          setChatsError(msg);
          if (!append) {
            setChats([]);
            setChatsTotal(0);
          }
          toast.error(msg);
        }
      } finally {
        setChatsLoading(false);
        setChatsLoadingMore(false);
      }
    },
    [searchQuery],
  );

  const applyResponseWindowFromPayload = useCallback((payload) => {
    if (!payload) return;
    if (payload.sessionExpired == null && !payload.responseWindow) return;
    const rw = payload.responseWindow;
    const expired =
      payload.sessionExpired === true ||
      rw?.isExpired === true ||
      (rw != null && Number(rw.remainingSeconds) <= 0);
    setSessionExpired(Boolean(expired));
  }, []);

  const handleSidebarResponseWindow = useCallback((rw, meta = {}) => {
    if (meta.sessionExpired != null) {
      setSessionExpired(Boolean(meta.sessionExpired));
      return;
    }
    if (!rw) return;
    setSessionExpired(
      rw.isExpired === true || Number(rw.remainingSeconds) <= 0,
    );
  }, []);

  const bumpSidebarRefresh = useCallback(() => {
    setSidebarRefreshToken((n) => n + 1);
  }, []);

  const mergeChatIntoList = useCallback((incomingChat, chatId, message) => {
    const id = incomingChat?.chatId ?? chatId;
    if (!id) return;

    setChats((prev) => {
      const existing = prev.find((c) => c.chatId === id);
      const isActive = String(id) === String(activeChatIdRef.current);
      const merged = {
        ...(existing || {}),
        ...(incomingChat || {}),
        chatId: id,
        lastMessage: message ?? incomingChat?.lastMessage ?? existing?.lastMessage,
        updatedAt:
          incomingChat?.updatedAt ??
          message?.timestamp ??
          existing?.updatedAt,
        unreadCount: isActive
          ? 0
          : incomingChat?.unreadCount ??
            (Number(existing?.unreadCount) || 0) + 1,
      };
      const rest = prev.filter((c) => c.chatId !== id);
      return [merged, ...rest];
    });
  }, []);

  const handleSocketIncoming = useCallback(
    (payload) => {
      const chatId = payload?.chatId ?? payload?.chat?.chatId;
      const message = payload?.message;
      const chat = payload?.chat;

      if (!chatId) return;

      mergeChatIntoList(chat, chatId, message);

      const isActiveChat = String(chatId) === String(activeChatIdRef.current);

      if (isActiveChat && message) {
        const messageKey = getIncomingMessageKey(message);
        setMessages((prev) => {
          if (prev.some((m) => getIncomingMessageKey(m) === messageKey)) {
            return prev;
          }
          return sortMessagesChronologically([...prev, message]);
        });
        setMessagesTotal((total) => total + 1);
        shouldStickToBottomRef.current = true;

        markChatRead(chatId).catch(() => {});
        setChats((prev) =>
          prev.map((c) =>
            c.chatId === chatId ? { ...c, unreadCount: 0 } : c,
          ),
        );
        // Refresh response window / sidebar after customer message
        bumpSidebarRefresh();
        setSessionExpired(false);
      } else {
        playWhatsAppMessageSound();
      }
    },
    [mergeChatIntoList, bumpSidebarRefresh],
  );

  const handleSocketMessageStatus = useCallback((payload) => {
    const chatId = payload?.chatId;
    const metaMessageId =
      payload?.metaMessageId ?? payload?.messageId ?? payload?.wamid;
    const status = payload?.status ?? payload?.deliveryStatus;

    if (!chatId || !metaMessageId || !status) return;
    if (String(chatId) !== String(activeChatIdRef.current)) return;

    setMessages((prev) =>
      prev.map((m) => {
        const metaId = getOutgoingMessageMetaId(m);
        if (metaId != null && String(metaId) === String(metaMessageId)) {
          return { ...m, status };
        }
        return m;
      }),
    );
  }, []);

  useWhatsAppInboxSocket({
    enabled: true,
    onIncomingMessage: handleSocketIncoming,
    onMessageStatus: handleSocketMessageStatus,
    onConnectionChange: setSocketConnected,
  });

  const loadMessageWindow = useCallback(
    async (chatId, { silent = false, stickToBottom = true } = {}) => {
      if (!chatId) return;
      if (!silent) {
        setMessagesLoading(true);
        setMessagesError("");
      }

      try {
        const probe = await listMessages(chatId, {
          limit: 1,
          offset: 0,
        });
        const total =
          Number(probe?.total) ||
          (Array.isArray(probe?.data) ? probe.data.length : 0);

        if (total === 0) {
          setMessages([]);
          setMessagesTotal(0);
          setMessagesPageOffset(0);
          messagesPageOffsetRef.current = 0;
          applyResponseWindowFromPayload(probe);
          return;
        }

        const pageOffset = silent
          ? messagesPageOffsetRef.current
          : Math.max(0, total - MESSAGE_PAGE_SIZE);
        const fetchLimit = silent
          ? Math.max(MESSAGE_PAGE_SIZE, total - pageOffset)
          : MESSAGE_PAGE_SIZE;

        const pageRes = await listMessages(chatId, {
          limit: fetchLimit,
          offset: pageOffset,
        });

        applyResponseWindowFromPayload(probe);
        applyResponseWindowFromPayload(pageRes);

        const rows = sortMessagesChronologically(pageRes?.data || []);
        setMessages(rows);
        setMessagesTotal(total);
        setMessagesPageOffset(pageOffset);
        messagesPageOffsetRef.current = pageOffset;
        if (stickToBottom) {
          shouldStickToBottomRef.current = true;
        }
      } catch (err) {
        const msg = reportWhatsAppError(err, "Failed to load messages", {
          silent,
        });
        if (!silent) {
          setMessagesError(msg);
          setMessages([]);
          setMessagesTotal(0);
          setMessagesPageOffset(0);
          messagesPageOffsetRef.current = 0;
        }
      } finally {
        if (!silent) {
          setMessagesLoading(false);
        }
      }
    },
    [applyResponseWindowFromPayload],
  );

  const loadOlderMessages = useCallback(async () => {
    if (!selectedChatId || loadingOlder || messagesPageOffset <= 0) return;

    setLoadingOlder(true);
    const newOffset = Math.max(0, messagesPageOffset - MESSAGE_PAGE_SIZE);
    const fetchLimit = messagesPageOffset - newOffset;

    try {
      const res = await listMessages(selectedChatId, {
        limit: fetchLimit,
        offset: newOffset,
      });
      const rows = sortMessagesChronologically(res?.data || []);
      const el = messagesContainerRef.current;
      const prevScrollHeight = el?.scrollHeight || 0;

      setMessages((prev) => sortMessagesChronologically([...rows, ...prev]));
      setMessagesPageOffset(newOffset);
      messagesPageOffsetRef.current = newOffset;
      shouldStickToBottomRef.current = false;

      requestAnimationFrame(() => {
        if (el) {
          el.scrollTop = el.scrollHeight - prevScrollHeight;
        }
      });
    } catch (err) {
      reportWhatsAppError(err, "Failed to load older messages");
    } finally {
      setLoadingOlder(false);
    }
  }, [selectedChatId, loadingOlder, messagesPageOffset]);

  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;

    shouldStickToBottomRef.current = isNearBottom();

    if (
      el.scrollTop < SCROLL_LOAD_OLDER_PX &&
      messagesPageOffset > 0 &&
      !loadingOlder
    ) {
      loadOlderMessages();
    }
  }, [isNearBottom, messagesPageOffset, loadingOlder, loadOlderMessages]);

  useEffect(() => {
    if (!socketConnected) return;
    fetchChats({ offset: 0, append: false, silent: true });
    if (selectedChatId) {
      loadMessageWindow(selectedChatId, {
        silent: true,
        stickToBottom: isNearBottom(),
      });
    }
  }, [socketConnected, fetchChats, loadMessageWindow, selectedChatId, isNearBottom]);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchInput]);

  useEffect(() => {
    fetchChats({ offset: 0, append: false });
  }, [fetchChats]);

  useEffect(() => {
    const unlock = () => unlockWhatsAppNotificationSound();
    window.addEventListener("click", unlock, { once: true, passive: true });
    window.addEventListener("keydown", unlock, { once: true, passive: true });
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  useEffect(() => {
    resetMainScrollTop();
  }, []);

  useEffect(() => {
    if (socketConnected) {
      if (chatPollTimerRef.current) {
        clearInterval(chatPollTimerRef.current);
        chatPollTimerRef.current = null;
      }
      return undefined;
    }

    chatPollTimerRef.current = setInterval(() => {
      if (!socketConnectedRef.current) {
        fetchChats({ offset: 0, append: false, silent: true });
      }
    }, FALLBACK_POLL_MS);

    return () => {
      if (chatPollTimerRef.current) {
        clearInterval(chatPollTimerRef.current);
        chatPollTimerRef.current = null;
      }
    };
  }, [fetchChats, socketConnected]);

  useEffect(() => {
    if (!selectedChatId) {
      setMessages([]);
      setMessagesError("");
      setMessagesTotal(0);
      setMessagesPageOffset(0);
      messagesPageOffsetRef.current = 0;
      shouldStickToBottomRef.current = true;
      setSessionExpired(false);
      setDetailsOpen(false);
      setComposerText("");
      setEmojiPickerOpen(false);
      setAttachMenuOpen(false);
      setMediaModal({ show: false, type: "image" });
      return undefined;
    }

    shouldStickToBottomRef.current = true;
    setDetailsOpen(false);
    loadMessageWindow(selectedChatId);

    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }

    if (!socketConnected) {
      pollTimerRef.current = setInterval(() => {
        if (!socketConnectedRef.current) {
          loadMessageWindow(selectedChatId, {
            silent: true,
            stickToBottom: isNearBottom(),
          });
        }
      }, FALLBACK_POLL_MS);
    }

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [selectedChatId, loadMessageWindow, isNearBottom, socketConnected]);

  useEffect(() => {
    if (!selectedChatId || messagesLoading) return;
    if (shouldStickToBottomRef.current) {
      scrollMessagesToBottom("auto");
    }
  }, [
    selectedChatId,
    messagesLoading,
    messages,
    scrollMessagesToBottom,
  ]);

  const markingReadRef = useRef(false);

  useEffect(() => {
    markingReadRef.current = false;
  }, [selectedChatId]);

  useEffect(() => {
    if (!selectedChatId || markingReadRef.current) return;
    const chat = chats.find((c) => c.chatId === selectedChatId);
    if (!chat || !(Number(chat.unreadCount) > 0)) return;

    markingReadRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        await markChatRead(selectedChatId);
        if (!cancelled) {
          setChats((prev) =>
            prev.map((c) =>
              c.chatId === selectedChatId ? { ...c, unreadCount: 0 } : c,
            ),
          );
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(getWhatsAppErrorMessage(err, "Failed to mark as read"));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedChatId, chats]);

  const handleSelectChat = (chatId) => {
    navigate(`/whatsapp/inbox/${encodeURIComponent(chatId)}`);
  };

  const handleBackToList = () => {
    navigate("/whatsapp/inbox");
  };

  const handleSend = async () => {
    const text = composerText.trim();
    if (!text || !selectedChatId || sending || sessionExpired) return;

    setSending(true);
    try {
      const res = await sendTextMessage(selectedChatId, text);
      if (res?.success === false) {
        throw new Error(res.message || "Failed to send message");
      }
      setComposerText("");
      shouldStickToBottomRef.current = true;
      await loadMessageWindow(selectedChatId, { silent: true, stickToBottom: true });
      await fetchChats({ offset: 0, append: false });
      scrollMessagesToBottom("smooth");
    } catch (err) {
      const msg =
        err?.message || getWhatsAppErrorMessage(err, "Failed to send message");
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const handleComposerKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const insertComposerEmoji = useCallback((emoji) => {
    const textarea = composerTextareaRef.current;
    if (!textarea) {
      setComposerText((prev) => prev + emoji);
      return;
    }

    const start = textarea.selectionStart ?? composerText.length;
    const end = textarea.selectionEnd ?? composerText.length;
    const next =
      composerText.slice(0, start) + emoji + composerText.slice(end);
    setComposerText(next);

    requestAnimationFrame(() => {
      textarea.focus();
      const pos = start + emoji.length;
      textarea.setSelectionRange(pos, pos);
    });
  }, [composerText]);

  const handleMediaSent = useCallback(async (sendRes, meta) => {
    if (!selectedChatId) return;

    if (meta?.upload?.url) {
      const optimistic = {
        id: sendRes?.messageId || `temp-${Date.now()}`,
        direction: "OUTGOING",
        type: meta.type,
        timestamp: Math.floor(Date.now() / 1000),
        status: "SENT",
        body: {
          url: meta.upload.url,
          caption: meta.caption || undefined,
          ...(meta.type === "document"
            ? {
                filename: meta.filename,
                document: {
                  filename: meta.filename,
                  link: meta.upload.url,
                },
              }
            : {}),
          ...(meta.type === "image"
            ? {
                image: {
                  link: meta.upload.url,
                  caption: meta.caption || undefined,
                },
              }
            : {}),
          ...(meta.type === "video"
            ? {
                video: {
                  link: meta.upload.url,
                  caption: meta.caption || undefined,
                },
              }
            : {}),
          ...(meta.type === "audio"
            ? { audio: { link: meta.upload.url } }
            : {}),
        },
      };
      setMessages((prev) => sortMessagesChronologically([...prev, optimistic]));
      setMessagesTotal((total) => total + 1);
      shouldStickToBottomRef.current = true;
      scrollMessagesToBottom("smooth");
    }

    await loadMessageWindow(selectedChatId, {
      silent: true,
      stickToBottom: true,
    });
    await fetchChats({ offset: 0, append: false });
    bumpSidebarRefresh();
    scrollMessagesToBottom("smooth");
  }, [
    selectedChatId,
    loadMessageWindow,
    fetchChats,
    bumpSidebarRefresh,
    scrollMessagesToBottom,
  ]);

  const handleSessionExpired = useCallback(() => {
    setSessionExpired(true);
    setShowTemplateModal(true);
    setMediaModal({ show: false, type: "image" });
    setAttachMenuOpen(false);
  }, []);

  const toggleSoundMuted = () => {
    const next = !soundMuted;
    setSoundMuted(next);
    setWhatsAppSoundMuted(next);
    if (!next) {
      unlockWhatsAppNotificationSound();
    }
  };

  const hasMoreChats = chats.length < chatsTotal;
  const filteredChats = useMemo(() => {
    if (chatFilter === "unread") return chats.filter(isUnreadChat);
    if (chatFilter === "read") return chats.filter((c) => !isUnreadChat(c));
    return chats;
  }, [chats, chatFilter]);
  const emptyListMessage =
    chatFilter === "unread"
      ? "No unread chats"
      : chatFilter === "read"
        ? "No read chats"
        : "No conversations yet";
  const displayName = selectedChat
    ? getChatDisplayName(selectedChat)
    : selectedChatId
      ? formatWhatsAppMobile(selectedChatId)
      : "";
  const displayMobile = selectedChat
    ? formatCompactWhatsAppMobile(
        selectedChat.senderMobile || selectedChat.chatId,
      )
    : selectedChatId
      ? formatCompactWhatsAppMobile(selectedChatId)
      : "";

  const headerInitials = getInitials(displayName) || "?";

  return (
    <WhatsAppPageShell className="whatsapp-page-shell--fill">
      <div className="whatsapp-inbox-root">
      <div
        className={`whatsapp-inbox-layout ${selectedChatId ? "chat-open" : ""}`}
      >
        <aside className="whatsapp-chat-list-col">
          <div className="whatsapp-list-header">
            <div className="whatsapp-inbox-header-row">
              <h1>Chats</h1>
              <button
                type="button"
                className="whatsapp-icon-btn whatsapp-mute-btn"
                onClick={toggleSoundMuted}
                title={soundMuted ? "Unmute notifications" : "Mute notifications"}
                aria-label={soundMuted ? "Unmute notifications" : "Mute notifications"}
              >
                <Icon
                  icon={
                    soundMuted
                      ? "solar:volume-cross-line-duotone"
                      : "solar:volume-loud-line-duotone"
                  }
                  width={20}
                />
              </button>
            </div>
            <div className="whatsapp-search-wrap">
              <Icon
                icon="solar:magnifer-line-duotone"
                className="whatsapp-search-icon"
                width={18}
              />
              <input
                type="search"
                placeholder="Search chats..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Search chats"
              />
            </div>
            <div
              className="whatsapp-chat-filters"
              role="tablist"
              aria-label="Filter chats"
            >
              {CHAT_FILTERS.map((filter) => {
                const isActive = chatFilter === filter.id;
                return (
                  <button
                    key={filter.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`whatsapp-chat-filter-pill${
                      isActive ? " active" : ""
                    }`}
                    onClick={() => setChatFilter(filter.id)}
                  >
                    {filter.icon ? (
                      <Icon icon={filter.icon} width={16} height={16} />
                    ) : null}
                    <span>{filter.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="whatsapp-chat-list">
            {chatsLoading ? <ChatListSkeleton /> : null}

            {!chatsLoading && chatsError ? (
              <div className="whatsapp-list-error">{chatsError}</div>
            ) : null}

            {!chatsLoading && !chatsError && filteredChats.length === 0 ? (
              <div className="whatsapp-list-empty">{emptyListMessage}</div>
            ) : null}

            {!chatsLoading &&
              filteredChats.map((chat) => {
                const name = getChatDisplayName(chat);
                const preview = chat.lastMessage
                  ? getMessagePreviewText(chat.lastMessage)
                  : "";
                const time = formatRelativeTime(
                  chat.updatedAt || chat.lastMessage?.timestamp,
                );
                const isActive = chat.chatId === selectedChatId;

                return (
                  <button
                    key={chat.chatId}
                    type="button"
                    className={`whatsapp-chat-row ${isActive ? "active" : ""}`}
                    onClick={() => handleSelectChat(chat.chatId)}
                  >
                    <div className="whatsapp-avatar">{getInitials(name)}</div>
                    <div className="whatsapp-chat-meta">
                      <div className="whatsapp-chat-top">
                        <span className="whatsapp-chat-name">{name}</span>
                        <span className="whatsapp-chat-time">{time}</span>
                      </div>
                      <div className="whatsapp-chat-bottom">
                        <span className="whatsapp-chat-preview">{preview}</span>
                        {Number(chat.unreadCount) > 0 ? (
                          <span className="whatsapp-unread-badge">
                            {chat.unreadCount > 99 ? "99+" : chat.unreadCount}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </button>
                );
              })}

            {hasMoreChats && !chatsLoading ? (
              <button
                type="button"
                className="whatsapp-load-more"
                disabled={chatsLoadingMore}
                onClick={() =>
                  fetchChats({ offset: chats.length, append: true })
                }
              >
                {chatsLoadingMore ? "Loading..." : "Load more"}
              </button>
            ) : null}
          </div>
        </aside>

        <section className="whatsapp-conversation-col">
          {!selectedChatId ? (
            <div className="whatsapp-empty-state">
              <Icon
                icon="logos:whatsapp-icon"
                width={64}
                height={64}
                style={{ opacity: 0.5 }}
              />
              <p>Select a chat to start messaging</p>
            </div>
          ) : (
            <>
              <header className="whatsapp-conv-header wa-chat-header">
                <button
                  type="button"
                  className="whatsapp-conv-back"
                  onClick={handleBackToList}
                  aria-label="Back to chat list"
                >
                  <Icon icon="solar:arrow-left-line-duotone" width={22} />
                </button>
                <div className="wa-chat-avatar">{headerInitials}</div>
                <div className="whatsapp-conv-title flex-grow-1">
                  <h2>{displayName}</h2>
                  <p>{displayMobile}</p>
                </div>
                <button
                  type="button"
                  className="whatsapp-icon-btn wa-details-toggle"
                  onClick={() => setDetailsOpen(true)}
                  title="Chat details"
                  aria-label="Open chat details"
                >
                  <Icon icon="solar:info-circle-line-duotone" width={22} />
                </button>
              </header>

              {messagesLoading ? (
                <MessagesSkeleton />
              ) : (
                <div
                  ref={messagesContainerRef}
                  className="whatsapp-messages wa-chat-wallpaper"
                  onScroll={handleMessagesScroll}
                >
                  {loadingOlder ? (
                    <div className="whatsapp-load-older">Loading older messages...</div>
                  ) : null}
                  {messagesError ? (
                    <div className="whatsapp-list-error">{messagesError}</div>
                  ) : null}
                  {!messagesError && messages.length === 0 ? (
                    <div className="whatsapp-list-empty">
                      No messages yet. Say hello!
                    </div>
                  ) : null}
                  {messages.map((msg) => (
                    <MessageBubble
                      key={msg.id ?? `${msg.timestamp}-${msg.direction}`}
                      message={msg}
                      onImageClick={openImageModal}
                    />
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}

              {sessionExpired ? (
                <div className="wa-session-banner" role="status">
                  <Icon icon="solar:danger-triangle-line-duotone" width={18} />
                  <span>Session expired — use Template</span>
                  <button
                    type="button"
                    className="whatsapp-btn-primary whatsapp-btn-sm"
                    onClick={() => setShowTemplateModal(true)}
                  >
                    Send template
                  </button>
                </div>
              ) : null}

              <div className="whatsapp-composer wa-chat-composer">
                {sessionExpired ? (
                  <button
                    type="button"
                    className="whatsapp-btn-secondary wa-composer-template-btn"
                    onClick={() => setShowTemplateModal(true)}
                  >
                    <Icon icon="solar:letter-line-duotone" width={18} />
                    Send template
                  </button>
                ) : (
                  <>
                    <div className="wa-composer-tools">
                      <div className="wa-composer-popover-anchor">
                        <button
                          ref={emojiButtonRef}
                          type="button"
                          className={`wa-composer-tool ${emojiPickerOpen ? "active" : ""}`}
                          title="Emoji"
                          aria-label="Emoji"
                          aria-expanded={emojiPickerOpen}
                          onClick={() => {
                            setAttachMenuOpen(false);
                            setEmojiPickerOpen((open) => !open);
                          }}
                        >
                          <Icon icon="solar:smile-circle-linear" width={22} />
                        </button>
                        <ChatEmojiPicker
                          open={emojiPickerOpen}
                          onClose={() => setEmojiPickerOpen(false)}
                          onSelect={insertComposerEmoji}
                          anchorRef={emojiButtonRef}
                        />
                      </div>
                      <div className="wa-composer-popover-anchor">
                        <button
                          ref={attachButtonRef}
                          type="button"
                          className={`wa-composer-tool ${attachMenuOpen ? "active" : ""}`}
                          title="Attach"
                          aria-label="Attach"
                          aria-expanded={attachMenuOpen}
                          onClick={() => {
                            setEmojiPickerOpen(false);
                            setAttachMenuOpen((open) => !open);
                          }}
                        >
                          <Icon icon="solar:paperclip-linear" width={22} />
                        </button>
                        <ChatAttachmentMenu
                          open={attachMenuOpen}
                          onClose={() => setAttachMenuOpen(false)}
                          onSelect={(type) =>
                            setMediaModal({ show: true, type })
                          }
                          anchorRef={attachButtonRef}
                        />
                      </div>
                      <button
                        type="button"
                        className="wa-composer-tool"
                        title="Send template"
                        aria-label="Send template"
                        onClick={() => setShowTemplateModal(true)}
                      >
                        <Icon icon="mdi:hand-pointing-right" width={22} />
                      </button>
                      <button
                        type="button"
                        className="wa-composer-tool"
                        title="Quick replies"
                        aria-label="Quick replies"
                        onClick={() =>
                          toast("Quick replies coming soon", { icon: "🤖" })
                        }
                      >
                        <Icon icon="mdi:robot-outline" width={22} />
                      </button>
                    </div>
                    <div className="wa-composer-pill">
                      <textarea
                        ref={composerTextareaRef}
                        rows={1}
                        placeholder="Type a message"
                        value={composerText}
                        onChange={(e) => setComposerText(e.target.value)}
                        onKeyDown={handleComposerKeyDown}
                        onFocus={() => setAttachMenuOpen(false)}
                        disabled={sending}
                        aria-label="Message text"
                      />
                    </div>
                    {composerText.trim() ? (
                      <button
                        type="button"
                        className="wa-composer-send"
                        onClick={handleSend}
                        disabled={sending}
                        aria-label="Send message"
                      >
                        <Icon icon="solar:plain-2-bold" width={22} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="wa-composer-mic"
                        title="Voice message"
                        aria-label="Voice message"
                        onClick={() =>
                          toast("Voice messages coming soon", { icon: "🎤" })
                        }
                      >
                        <Icon icon="solar:microphone-bold" width={22} />
                      </button>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </section>

        {selectedChatId ? (
          <>
            <div
              className={`wa-sidebar-backdrop ${detailsOpen ? "open" : ""}`}
              onClick={() => setDetailsOpen(false)}
              aria-hidden={!detailsOpen}
            />
            <ChatRightSidebar
              chatId={selectedChatId}
              open={detailsOpen}
              onClose={() => setDetailsOpen(false)}
              onResponseWindowChange={handleSidebarResponseWindow}
              refreshToken={sidebarRefreshToken}
              onImagePreview={openImageModal}
            />
          </>
        ) : null}
      </div>
    </div>

    <SendTemplateModal
      show={showTemplateModal}
      onHide={() => setShowTemplateModal(false)}
      chatId={selectedChatId}
      onSent={async () => {
        if (!selectedChatId) return;
        await loadMessageWindow(selectedChatId, {
          silent: true,
          stickToBottom: true,
        });
        await fetchChats({ offset: 0, append: false });
        bumpSidebarRefresh();
        scrollMessagesToBottom("smooth");
      }}
    />

    <WhatsAppImagePreviewModal
      show={imageModal.isOpen}
      imageUrl={imageModal.imageUrl}
      alt={imageModal.alt}
      onHide={closeImageModal}
    />

    <SendChatMediaModal
      show={mediaModal.show}
      type={mediaModal.type}
      chatId={selectedChatId}
      sessionExpired={sessionExpired}
      onHide={() => setMediaModal({ show: false, type: "image" })}
      onSent={handleMediaSent}
      onSessionExpired={handleSessionExpired}
    />
    </WhatsAppPageShell>
  );
}
